import { css } from 'aphrodite';
import * as React from 'react';
import { IPlatformVersionChange } from '../../../../models/utils/platformCustomizations';
import { getSocialMediaPlatformLogoUrl } from '../../../../models/utils/socialUtils';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { SimpleTooltip } from '../../Tooltip';
import { styleSheet } from './styles';

interface IProps {
	/** Versions that no longer match the base post — the work a discard would lose. */
	changes: IPlatformVersionChange[];
	isSplit: boolean;
	onChange: (customizeByPlatform: boolean) => void;
	/** Whether a discard would publish media on a platform whose version left it out. */
	spreadsMedia?: boolean;
}

const describeChange = ({ media, wording }: IPlatformVersionChange) =>
	wording && media ? 'wording and media' : wording ? 'wording' : 'media';

const describeToggle = (isSplit: boolean, editedCount: number) => {
	if (!isSplit) {
		return 'Write a separate version for each platform';
	}
	if (!editedCount) {
		return 'Go back to one post for every platform';
	}
	return `Turning this off discards ${editedCount === 1 ? 'the edited version' : `all ${editedCount} edited versions`}`;
};

export const PlatformCustomizationToggle: React.FC<IProps> = ({ changes, isSplit, onChange, spreadsMedia }) => {
	const [confirmingDiscard, setConfirmingDiscard] = React.useState(false);

	const onToggle = (nextIsSplit: boolean) => {
		// Turning it off discards every version, and nothing keeps a copy — so ask first, but only
		// when there is wording to lose.
		if (!nextIsSplit && changes.length > 0) {
			setConfirmingDiscard(true);
			return;
		}
		onChange(nextIsSplit);
	};

	const onConfirmDiscard = () => {
		setConfirmingDiscard(false);
		onChange(false);
	};

	return (
		<>
			<SimpleTooltip title={describeToggle(isSplit, changes.length)}>
				<div className={css(styleSheet.toggleBeam)}>
					<Button
						aria-label='Customize by platform'
						aria-pressed={isSplit}
						onClick={() => onToggle(!isSplit)}
						size='sm'
						styles={[styleSheet.toggleButton]}
						type='button'
						variant='ghost'
					>
						<span>Customize by platform</span>
						<span aria-hidden='true' className={css(styleSheet.toggleTrack, isSplit && styleSheet.toggleTrackActive)}>
							<span className={css(styleSheet.toggleThumb, isSplit && styleSheet.toggleThumbActive)} />
						</span>
					</Button>
				</div>
			</SimpleTooltip>
			<Dialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Discard your platform versions?</DialogTitle>
						<DialogDescription>
							Every platform goes back to publishing the base post. This can&apos;t be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<div className={css(styleSheet.discardDialogBody)}>
							<span>You&apos;ll lose the changes made for:</span>
							<ul className={css(styleSheet.discardDialogList)}>
								{changes.map(change => (
									<li className={css(styleSheet.discardDialogItem)} key={change.platform}>
										<img
											alt=''
											className={css(styleSheet.discardDialogLogo)}
											src={getSocialMediaPlatformLogoUrl(change.platform)}
										/>
										<span>{`${change.platform}: ${describeChange(change)}`}</span>
									</li>
								))}
							</ul>
							{spreadsMedia ? <span>Media you left off a platform will publish on every platform.</span> : null}
						</div>
					</DialogBody>
					<DialogFooter>
						<Button variant='secondary' onClick={() => setConfirmingDiscard(false)}>
							Keep them
						</Button>
						<Button variant='destructive' onClick={onConfirmDiscard}>
							Discard versions
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</>
	);
};
